import { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { TbBell, TbBellRinging, TbCamera } from 'react-icons/tb';
import { AuthContext } from '../auth/AuthContext';
import { communityApi as defaultApi } from './community-api';
import { ProfileAvatarLink } from './PostCard';
import './community.css';

function relativeTime(createdAt) {
  const timestamp = new Date(createdAt).getTime();
  if (!Number.isFinite(timestamp)) return '';
  const minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60000));
  if (minutes < 1) return 'ahora';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  return `hace ${Math.floor(hours / 24)} d`;
}

function notificationText(notification) {
  if (notification.type === 'announcement' && notification.announcementCode === 'profile_photo') return 'Ahora podés subir tu foto de perfil';
  const actor = notification.actor?.username || 'Alguien';
  if (notification.type === 'comment') return `${actor} comentó tu publicación`;
  if (notification.type === 'reaction') return `${actor} reaccionó a tu publicación`;
  if (notification.type === 'follow') return `${actor} empezó a seguirte`;
  return `${actor} realizó una actividad en la comunidad`;
}

export function NotificationsBell({ api = defaultApi }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const markedOnOpen = useRef(false);
  const unreadCount = notifications.filter(notification => !notification.readAt).length;
  const unread = notifications.filter(notification => !notification.readAt);
  const previous = notifications.filter(notification => notification.readAt);

  useEffect(() => {
    let active = true;
    async function loadAllNotifications() {
      const allNotifications = [];
      const seenCursors = new Set();
      let cursor;
      do {
        if (cursor && seenCursors.has(cursor)) throw new Error('Cursor de notificaciones repetido.');
        if (cursor) seenCursors.add(cursor);
        const result = await api.notifications(cursor);
        allNotifications.push(...(result.notifications || []));
        cursor = result.nextCursor || null;
      } while (cursor);
      if (active) {
        setNotifications(allNotifications);
        setLoadFailed(false);
      }
    }

    loadAllNotifications()
      .catch(() => {
        if (active) {
          setLoadFailed(true);
          setError('No se pudieron cargar las notificaciones.');
        }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api]);

  useEffect(() => {
    if (!open) {
      markedOnOpen.current = false;
      return;
    }
    if (loading || loadFailed || markedOnOpen.current) return;
    markedOnOpen.current = true;
    api.markNotificationsRead({ all: true })
      .then(() => {
        setError('');
        setNotifications(current => current.map(notification => ({ ...notification, readAt: notification.readAt || new Date().toISOString() })));
      })
      .catch(() => setError('No se pudieron marcar como leídas.'));
  }, [api, loadFailed, loading, open]);

  return <div className="community-notifications">
    <button
      type="button"
      className="gym-button gym-button--secondary community-notifications__trigger"
      aria-label={`Notificaciones${unreadCount ? `, ${unreadCount} sin leer` : ''}`}
      aria-expanded={open}
      aria-controls="community-notifications-panel"
      onClick={() => setOpen(current => !current)}
    >
      <TbBell aria-hidden="true" focusable="false" />
      {unreadCount > 0 && <span className="community-notifications__count" aria-hidden="true">{unreadCount > 99 ? '99+' : unreadCount}</span>}
    </button>
    {open && <section id="community-notifications-panel" className="community-notifications__panel" aria-label="Notificaciones">
      <h2>Notificaciones</h2>
      {error && <p className="gym-state gym-state--error" role="alert">{error}</p>}
      {loading && <p className="gym-state gym-state--loading" role="status">Cargando notificaciones…</p>}
      {!loading && notifications.length === 0 && <div className="community-notifications__empty">
        <span className="community-notifications__empty-icon">
          <TbBellRinging role="img" aria-label="Sin notificaciones" aria-hidden="false" focusable="false" />
        </span>
        <p>No tenés notificaciones nuevas.</p>
      </div>}
      {!loading && notifications.length > 0 && <div className="community-notifications__groups">
        {unread.length > 0 && <section className="community-notifications__group" aria-label="Nuevas">
          <h3>Nuevas · {unread.length}</h3>
          <ul>{unread.map(notification => <NotificationRow key={notification.id} notification={notification} unread onNavigate={() => setOpen(false)} />)}</ul>
        </section>}
        {previous.length > 0 && <section className="community-notifications__group" aria-label="Anteriores">
          <h3>Anteriores</h3>
          <ul>{previous.map(notification => <NotificationRow key={notification.id} notification={notification} onNavigate={() => setOpen(false)} />)}</ul>
        </section>}
      </div>}
    </section>}
  </div>;
}

function NotificationRow({ notification, unread = false, onNavigate }) {
  // Contexto opcional: sólo el aviso de foto necesita el id propio.
  const viewerId = useContext(AuthContext)?.user?.id;
  const actor = notification.actor;
  const isPhotoAnnouncement = notification.type === 'announcement' && notification.announcementCode === 'profile_photo';
  let target = '/comunidad';
  if (notification.type === 'follow' && actor?.id) target = `/comunidad/perfil/${actor.id}`;
  else if (isPhotoAnnouncement && viewerId) target = `/comunidad/perfil/${viewerId}`;
  const initial = (actor?.username || 'A').trim().charAt(0).toUpperCase() || 'A';
  return <li data-unread={unread ? 'true' : 'false'}>
    {actor?.id
      ? <ProfileAvatarLink person={actor} className="community-notifications__avatar" linkClassName="community-notifications__avatar-link" onNavigate={onNavigate} />
      : <span className="community-notifications__avatar" aria-hidden="true">{isPhotoAnnouncement ? <TbCamera focusable="false" /> : initial}</span>}
    <Link className="community-notifications__main" to={target} onClick={onNavigate}>
      <span className="community-notifications__copy">
        <span>{notificationText(notification)}</span>
        <time dateTime={notification.createdAt}>{relativeTime(notification.createdAt)}</time>
      </span>
      {unread && <span className="community-notifications__unread-dot" aria-label="Sin leer" />}
    </Link>
  </li>;
}
